import { useEffect, useRef, useCallback } from "react";
import WallpaperRenderer from "../renderer/WallpaperRenderer";

const PREVIEW_W = 360;
const PREVIEW_H = 800;

export function useWallpaperRenderer(bgSrc, fgSrc, clockSettings) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const tickTimer = useRef(null);
  const updateFrame = useRef(null);
  // Keep latest values so the tick closure doesn't go stale
  const latestBgRef = useRef(bgSrc);
  const latestFgRef = useRef(fgSrc);
  const latestCsRef = useRef(clockSettings);

  // Initialise renderer once and attach its canvas to the container div
  useEffect(() => {
    const renderer = new WallpaperRenderer(PREVIEW_W, PREVIEW_H);
    rendererRef.current = renderer;

    const canvas = renderer.domElement;
    // Keep the interactive buffer at display size. Full resolution is only
    // needed when exporting, where a separate renderer is created below.
    canvas.style.width = `${PREVIEW_W}px`;
    canvas.style.height = `${PREVIEW_H}px`;
    canvas.style.display = "block";
    canvas.style.imageRendering = "auto";

    if (containerRef.current) {
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(canvas);
    }

    return () => {
      if (tickTimer.current) {
        clearTimeout(tickTimer.current);
        clearInterval(tickTimer.current);
      }
      if (updateFrame.current !== null) {
        cancelAnimationFrame(updateFrame.current);
      }
      renderer.dispose();
      rendererRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Coalesce rapid slider changes to the browser's paint cadence. Repainting
  // more often than the display can present only blocks the next input event.
  useEffect(() => {
    latestBgRef.current = bgSrc;
    latestFgRef.current = fgSrc;
    latestCsRef.current = clockSettings;

    if (!rendererRef.current) return;
    if (updateFrame.current !== null) {
      cancelAnimationFrame(updateFrame.current);
    }
    updateFrame.current = requestAnimationFrame(() => {
      updateFrame.current = null;
      if (rendererRef.current) {
        rendererRef.current.update(
          latestBgRef.current,
          latestFgRef.current,
          latestCsRef.current
        );
      }
    });

    return () => {
      if (updateFrame.current !== null) {
        cancelAnimationFrame(updateFrame.current);
        updateFrame.current = null;
      }
    };
  }, [bgSrc, fgSrc, clockSettings]);

  // Clock tick disabled — admin panel uses static preview time instead of ticking live clock
  useEffect(() => {
    return () => {
      if (tickTimer.current) {
        clearTimeout(tickTimer.current);
        clearInterval(tickTimer.current);
      }
    };
  }, [clockSettings?.showSeconds]);

  const exportBlob = useCallback(async () => {
    if (!rendererRef.current) throw new Error("Renderer not ready");

    const exportRenderer = new WallpaperRenderer();
    try {
      await exportRenderer.update(
        latestBgRef.current,
        latestFgRef.current,
        latestCsRef.current
      );
      return await exportRenderer.exportBlob();
    } finally {
      exportRenderer.dispose();
    }
  }, []);

  return { containerRef, exportBlob, rendererRef };
}
